import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { addMapboxBaseLayers } from "../helpers/mapbox_base_layers"

const METERS_PER_MILE = 1609.344

// Two-way binding between a map (draggable marker + radius circle) and the
// latitude / longitude / proximity radius fields on the administration catalog
// form. Clicking the map sets the center; the slider sets the radius.
export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "radius", "slider", "radiusLabel"]

  connect() {
    // Default view: continental US, low zoom, until a center is picked.
    this.map = L.map(this.mapTarget).setView([39.8283, -98.5795], 4)
    addMapboxBaseLayers(this.map)
    this.map.on("click", (e) => this.setCenter(e.latlng.lat, e.latlng.lng))

    this.syncSlider()
    this.draw()
    this.fit()
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  // data-action: input->catalog-area-picker#fieldsChanged
  fieldsChanged() {
    this.syncSlider()
    this.draw()
    this.fit()
  }

  // data-action: input->catalog-area-picker#sliderChanged
  sliderChanged() {
    this.radiusTarget.value = this.sliderTarget.value
    this.updateRadiusLabel()
    this.draw()
  }

  // data-action: change->catalog-area-picker#fit
  fit() {
    if (this.circle) {
      this.map.fitBounds(this.circle.getLatLng().toBounds(this.circle.getRadius() * 2), { padding: [20, 20] })
    } else if (this.marker) {
      this.map.setView(this.marker.getLatLng(), 10)
    }
  }

  // data-action: catalog-area-picker#clear
  clear() {
    this.latitudeTarget.value = ""
    this.longitudeTarget.value = ""
    this.radiusTarget.value = ""
    this.syncSlider()
    this.draw()
  }

  setCenter(lat, lng) {
    this.latitudeTarget.value = Number(lat).toFixed(6)
    this.longitudeTarget.value = Number(lng).toFixed(6)
    // The model wants all three fields or none, so a first click also takes
    // the slider's radius.
    if (isNaN(this.radius)) this.radiusTarget.value = this.sliderTarget.value
    this.syncSlider()
    this.draw()
  }

  draw() {
    const center = this.center

    if (!center) {
      if (this.marker) this.map.removeLayer(this.marker)
      if (this.circle) this.map.removeLayer(this.circle)
      this.marker = this.circle = null
      return
    }

    if (this.marker) {
      this.marker.setLatLng(center)
    } else {
      this.marker = L.marker(center, { draggable: true }).addTo(this.map)
      this.marker.on("drag", () => {
        if (this.circle) this.circle.setLatLng(this.marker.getLatLng())
      })
      this.marker.on("dragend", () => {
        const pos = this.marker.getLatLng()
        this.setCenter(pos.lat, pos.lng)
      })
    }

    const radius = this.radius
    if (radius > 0) {
      if (this.circle) {
        this.circle.setLatLng(center)
        this.circle.setRadius(radius * METERS_PER_MILE)
      } else {
        this.circle = L.circle(center, {
          radius: radius * METERS_PER_MILE,
          color: "#3388ff",
          weight: 2,
          opacity: 0.7,
          fillOpacity: 0.15,
          interactive: false // let clicks inside the circle reach the map
        }).addTo(this.map)
      }
    } else if (this.circle) {
      this.map.removeLayer(this.circle)
      this.circle = null
    }
  }

  // Mirrors the radius field onto the slider, widening the slider's range for
  // a radius beyond it. The slider stays off until there is a center.
  syncSlider() {
    const radius = this.radius
    if (radius > 0) {
      if (radius > Number(this.sliderTarget.max)) this.sliderTarget.max = Math.ceil(radius)
      this.sliderTarget.value = radius
    }
    this.sliderTarget.disabled = !this.center
    this.updateRadiusLabel()
  }

  updateRadiusLabel() {
    const radius = this.radius
    this.radiusLabelTarget.textContent = radius > 0 ? `${radius} mi` : "—"
  }

  get center() {
    const lat = parseFloat(this.latitudeTarget.value)
    const lng = parseFloat(this.longitudeTarget.value)
    return isNaN(lat) || isNaN(lng) ? null : L.latLng(lat, lng)
  }

  get radius() {
    return parseFloat(this.radiusTarget.value)
  }
}
